import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import AppLayout from "../../components/AppLayout";
import SignInCard from "./SignInCard";
import LoginLinksCard from "./LoginLinksCard";
import StorageCard from "./StorageCard";
import Users from "./Users";
import AddressesCard from "./AddressesCard";
import TimezoneCard from "./TimezoneCard";
import StoredFilesCard from "./StoredFilesCard";
import UsageCard from "./UsageCard";
import MailCard from "./MailCard";
import ArchivedReleasesCard from "./ArchivedReleasesCard";
import InactiveProjectsCard from "./InactiveProjectsCard";
import FieldsCard from "./FieldsCard";
import AnnouncementsCard from "./AnnouncementsCard";
import { AdminLog } from "../log/LogView";

// /admin/:section — the administrator's area (realm role "admin" only; the backend answers 403
// to anyone else on every /api/admin route, so this check only avoids showing a dead page).
// Keycloak, S3 storage, accounts and their permissions are all set here, not in environment
// variables. Project memberships are given by each project's curators on the project page.
//
// Each section is its own route, so it has its own page title and h1 (WCAG 2.4.2).

export const SECTIONS = [
  { key: "users", label: "Users", Body: Users },
  { key: "sign-in", label: "Sign-in", Body: SignInCard },
  { key: "sign-in-links", label: "Sign-in page links", Body: LoginLinksCard },
  { key: "storage", label: "Storage", Body: StorageCard },
  { key: "fields", label: "Field templates", Body: FieldsCard },
  { key: "addresses", label: "Project addresses", Body: AddressesCard },
  { key: "time-zone", label: "Time zone", Body: TimezoneCard },
  { key: "stored-files", label: "Stored files", Body: StoredFilesCard },
  { key: "usage", label: "Usage and quotas", Body: UsageCard },
  { key: "mail", label: "E-mail", Body: MailCard },
  { key: "announcements", label: "Announcements", Body: AnnouncementsCard },
  { key: "inactive-projects", label: "Inactive projects", Body: InactiveProjectsCard },
  { key: "archived-releases", label: "Archived releases", Body: ArchivedReleasesCard },
  { key: "log", label: "Log", Body: AdminLog },
];

function SectionTabs({ active }) {
  return (
    <nav aria-label="Management sections" style={{ marginBottom: 20 }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb" }}>
        {SECTIONS.map(s => {
          const current = s.key === active;
          return (
            <li key={s.key}>
              <Link to={`/admin/${s.key}`} aria-current={current ? "page" : undefined}
                    style={{ display: "inline-block", padding: "8px 14px", fontSize: 14, textDecoration: "none",
                             fontWeight: current ? 700 : 500, color: current ? "var(--dp-accent-dark)" : "#374151",
                             borderBottom: current ? "3px solid var(--dp-accent-dark)" : "3px solid transparent",
                             marginBottom: -1 }}>
                {s.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default function ManagementPage() {
  const { section } = useParams();
  const { user } = useAuth();
  const current = SECTIONS.find(s => s.key === section);

  if (!user?.is_admin) {
    return (
      <AppLayout title="Management">
        <p role="alert" style={{ fontSize: 14, color: "#374151" }}>
          Management is for administrators. Ask an administrator if you need a permission or a
          setting changed; a project's curators add you to their project.
        </p>
      </AppLayout>
    );
  }
  if (!current) return <Navigate to="/admin/users" replace />;

  const { Body } = current;
  return (
    <AppLayout title={current.label} crumbs={[{ label: "Management", to: "/admin/users" }, { label: current.label }]}>
      <SectionTabs active={current.key} />
      <Body />
    </AppLayout>
  );
}
